import type { FC } from 'react'
import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { formatDistanceToNow } from 'date-fns'
import type { InAppNotification } from '../api/types'
import { useBatchImportLabel } from '../hooks/useBatchImportLabel'
import { getNotificationVisual } from '../utils/getNotificationVisual'
import { getNotificationCopyParts } from '../utils/getNotificationCopy'
import { getLocale } from '@/utils'
import { useAuth } from '@/hooks/use-auth'
import useTranslate from '@/hooks/use-translate'
import { getBadgeTokens } from '@/theme/badge-tokens'
import { densityRem } from '@/theme/base/density'

type NotificationItemProps = {
  notification: InAppNotification
  onClick: (notification: InAppNotification) => void
}

export const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const theme = useTheme()
  const { user } = useAuth()
  const { t } = useTranslate('notifications')
  const hasDossier = Boolean(notification.batchImportId)
  const {
    reference,
    name,
    isLoading: isDossierLoading,
    isError: isDossierError,
  } = useBatchImportLabel(notification.batchImportId)
  const visual = useMemo(
    () => getNotificationVisual(notification),
    [notification]
  )
  const toneColors = getBadgeTokens(theme.palette.mode)[visual.tone]
  const isUnread = notification.readAt == null
  const copy = getNotificationCopyParts(t, notification)
  const relativeTime = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: getLocale(user?.profile?.language),
  })

  // Le dossier est le sujet de la notification : son nom en titre, la référence
  // Nabu à côté, ou seule quand le dossier n'a pas de nom.
  const dossierTitle =
    name ??
    reference ??
    (isDossierError ? t('dossierNotFound') : t('dossierUnnamed'))
  const dossierReference = name ? reference : null

  const message = (
    <>
      {copy.highlight && (
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {copy.highlight}{' '}
        </Box>
      )}
      {copy.message}
    </>
  )

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(notification)}
      sx={{
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        display: 'flex',
        font: 'inherit',
        gap: 1.25,
        px: 2,
        py: 1.25,
        textAlign: 'left',
        width: '100%',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: toneColors.bg,
            borderRadius: 1,
            color: toneColors.fg,
            display: 'flex',
            height: 28,
            justifyContent: 'center',
            width: 28,
          }}
        >
          <SvgIcon sx={{ fontSize: 14 }}>
            <visual.Icon />
          </SvgIcon>
        </Box>
        {isUnread && (
          <Box
            aria-hidden
            data-testid="notification-unread-dot"
            sx={{
              backgroundColor: 'primary.main',
              border: '2px solid',
              borderColor: 'background.paper',
              borderRadius: '50%',
              height: 9,
              position: 'absolute',
              right: -3,
              top: -3,
              width: 9,
            }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            alignItems: 'baseline',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            component="p"
            noWrap
            sx={{
              color: 'text.primary',
              flex: 1,
              fontSize: densityRem(0.8125),
              fontWeight: hasDossier ? (isUnread ? 600 : 500) : 400,
              lineHeight: 1.45,
              m: 0,
              minWidth: 0,
            }}
          >
            {!hasDossier ? (
              message
            ) : isDossierLoading ? (
              <Skeleton variant="text" width="60%" />
            ) : (
              <>
                {dossierTitle}
                {dossierReference && (
                  <Box
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: densityRem(0.75),
                      fontWeight: 400,
                      ml: 0.75,
                    }}
                  >
                    {dossierReference}
                  </Box>
                )}
              </>
            )}
          </Typography>
          <Typography
            component="span"
            sx={{
              color: 'text.secondary',
              flexShrink: 0,
              fontSize: densityRem(0.6875),
              whiteSpace: 'nowrap',
            }}
          >
            {relativeTime}
          </Typography>
        </Box>
        {hasDossier && (
          <Typography
            component="p"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              fontSize: densityRem(0.8125),
              lineHeight: 1.45,
              m: 0,
              mt: 0.25,
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: '2',
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
